import React from "react";
import {
  CheckSquare,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CalendarDays,
  Phone,
  MessageSquare,
  Mail,
  Users,
} from "lucide-react";
import { isToday, isPast, isFuture, parseISO } from "date-fns";
import { formatPercent } from "./dashboardUtils";
import type { Task } from "../types";

interface TasksPerformanceCardProps {
  tasks: Task[];
}

export const TasksPerformanceCard: React.FC<TasksPerformanceCardProps> = ({
  tasks,
}) => {
  const stats = React.useMemo(() => {
    let completed = 0;
    let late = 0;
    let today = 0;
    let upcoming = 0;

    const typeCounts: Record<string, number> = {};

    tasks.forEach((t) => {
      // Tipo de tarefa
      const type = (t.type || "outros").toLowerCase();
      typeCounts[type] = (typeCounts[type] || 0) + 1;

      // Status
      if (t.done_date) {
        completed += 1;
      } else if (t.due_date) {
        try {
          const date = parseISO(t.due_date);
          if (isToday(date)) {
            today += 1;
          } else if (isPast(date)) {
            late += 1;
          } else if (isFuture(date)) {
            upcoming += 1;
          }
        } catch {
          upcoming += 1;
        }
      } else {
        upcoming += 1;
      }
    });

    const total = tasks.length;
    const completionRate = total > 0 ? (completed / total) * 100 : 0;

    return {
      total,
      completed,
      late,
      today,
      upcoming,
      completionRate,
      typeCounts,
    };
  }, [tasks]);

  const taskTypeIcons: Record<string, React.ReactNode> = {
    chamada: <Phone className="w-3.5 h-3.5 text-blue-500" />,
    reuniao: <Users className="w-3.5 h-3.5 text-indigo-500" />,
    whatsapp: <MessageSquare className="w-3.5 h-3.5 text-emerald-500" />,
    email: <Mail className="w-3.5 h-3.5 text-purple-500" />,
  };

  return (
    <div className="bg-card border border-border/80 rounded-xl p-4 shadow-xs">
      <div className="flex items-center justify-between border-b border-border/50 pb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <CheckSquare className="w-3.5 h-3.5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">
              Desempenho de Tarefas
            </h3>
            <p className="text-[11px] text-muted-foreground">
              Acompanhamento de prazos, entregas e execução das atividades
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
          {stats.total} tarefas
        </span>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Concluídas */}
        <div className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-1">
          <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold">Concluídas</span>
          </div>
          <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 block">
            {stats.completed}
          </span>
          <span className="text-[10px] text-muted-foreground block">
            {formatPercent(stats.completionRate)} do total
          </span>
        </div>

        {/* Para Hoje */}
        <div className="p-3 rounded-xl border border-blue-500/20 bg-blue-500/5 space-y-1">
          <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold">Para Hoje</span>
          </div>
          <span className="text-xl font-bold text-blue-600 dark:text-blue-400 block">
            {stats.today}
          </span>
          <span className="text-[10px] text-muted-foreground block">
            Atividades prioritárias
          </span>
        </div>

        {/* Em Atraso */}
        <div className="p-3 rounded-xl border border-rose-500/20 bg-rose-500/5 space-y-1">
          <div className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold">Em Atraso</span>
          </div>
          <span className="text-xl font-bold text-rose-600 dark:text-rose-400 block">
            {stats.late}
          </span>
          <span className="text-[10px] text-muted-foreground block">
            Requer atenção
          </span>
        </div>

        {/* Futuras */}
        <div className="p-3 rounded-xl border border-border/50 bg-muted/20 space-y-1">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <CalendarDays className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold">Futuras</span>
          </div>
          <span className="text-xl font-bold text-foreground block">
            {stats.upcoming}
          </span>
          <span className="text-[10px] text-muted-foreground block">
            Agendadas
          </span>
        </div>
      </div>

      {/* Tipos de Tarefas */}
      {Object.keys(stats.typeCounts).length > 0 && (
        <div className="mt-4 pt-3 border-t border-border/40">
          <span className="text-[11px] font-semibold text-muted-foreground block mb-2">
            Distribuição por tipo de atividade:
          </span>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.typeCounts).map(([type, count]) => (
              <div
                key={type}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-border/50 bg-muted/30 text-xs"
              >
                {taskTypeIcons[type] || (
                  <CheckSquare className="w-3.5 h-3.5 text-muted-foreground" />
                )}
                <span className="capitalize text-foreground font-medium">
                  {type}:
                </span>
                <span className="font-bold text-primary">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
